/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRemoteSessionIssuersQueryParamTier,
  ListRemoteSessionIssuersRequest,
  ListRemoteSessionIssuersSecurity,
} from "../models/operations/listremotesessionissuers.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRemoteSessionIssuersInfiniteQuery,
  buildRemoteSessionIssuersQuery,
  prefetchRemoteSessionIssuers,
  prefetchRemoteSessionIssuersInfinite,
  queryKeyRemoteSessionIssuers,
  queryKeyRemoteSessionIssuersInfinite,
  RemoteSessionIssuersInfiniteQueryData,
  RemoteSessionIssuersPageParams,
  RemoteSessionIssuersQueryData,
} from "./remoteSessionIssuers.core.js";
export {
  buildRemoteSessionIssuersInfiniteQuery,
  buildRemoteSessionIssuersQuery,
  prefetchRemoteSessionIssuers,
  prefetchRemoteSessionIssuersInfinite,
  queryKeyRemoteSessionIssuers,
  queryKeyRemoteSessionIssuersInfinite,
  type RemoteSessionIssuersInfiniteQueryData,
  type RemoteSessionIssuersPageParams,
  type RemoteSessionIssuersQueryData,
};

export type RemoteSessionIssuersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRemoteSessionIssuers remoteSessionIssuers
 *
 * @remarks
 * List the remote_session_issuers the caller's project can use: its own, plus those inherited from its organization and from the platform catalog. Newest first; the search, upstream_host and tier filters narrow the listing without changing its order or cursor.
 */
export function useRemoteSessionIssuers(
  request?: ListRemoteSessionIssuersRequest | undefined,
  security?: ListRemoteSessionIssuersSecurity | undefined,
  options?: QueryHookOptions<
    RemoteSessionIssuersQueryData,
    RemoteSessionIssuersQueryError
  >,
): UseQueryResult<
  RemoteSessionIssuersQueryData,
  RemoteSessionIssuersQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildRemoteSessionIssuersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRemoteSessionIssuers remoteSessionIssuers
 *
 * @remarks
 * List the remote_session_issuers the caller's project can use: its own, plus those inherited from its organization and from the platform catalog. Newest first; the search, upstream_host and tier filters narrow the listing without changing its order or cursor.
 */
export function useRemoteSessionIssuersSuspense(
  request?: ListRemoteSessionIssuersRequest | undefined,
  security?: ListRemoteSessionIssuersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RemoteSessionIssuersQueryData,
    RemoteSessionIssuersQueryError
  >,
): UseSuspenseQueryResult<
  RemoteSessionIssuersQueryData,
  RemoteSessionIssuersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRemoteSessionIssuersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRemoteSessionIssuers remoteSessionIssuers
 *
 * @remarks
 * List the remote_session_issuers the caller's project can use: its own, plus those inherited from its organization and from the platform catalog. Newest first; the search, upstream_host and tier filters narrow the listing without changing its order or cursor.
 */
export function useRemoteSessionIssuersInfinite(
  request?: ListRemoteSessionIssuersRequest | undefined,
  security?: ListRemoteSessionIssuersSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    RemoteSessionIssuersInfiniteQueryData,
    RemoteSessionIssuersQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    RemoteSessionIssuersInfiniteQueryData,
    RemoteSessionIssuersPageParams
  >,
  RemoteSessionIssuersQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    RemoteSessionIssuersInfiniteQueryData,
    RemoteSessionIssuersQueryError,
    InfiniteData<
      RemoteSessionIssuersInfiniteQueryData,
      RemoteSessionIssuersPageParams
    >,
    QueryKey,
    RemoteSessionIssuersPageParams
  >({
    ...buildRemoteSessionIssuersInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listRemoteSessionIssuers remoteSessionIssuers
 *
 * @remarks
 * List the remote_session_issuers the caller's project can use: its own, plus those inherited from its organization and from the platform catalog. Newest first; the search, upstream_host and tier filters narrow the listing without changing its order or cursor.
 */
export function useRemoteSessionIssuersInfiniteSuspense(
  request?: ListRemoteSessionIssuersRequest | undefined,
  security?: ListRemoteSessionIssuersSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    RemoteSessionIssuersInfiniteQueryData,
    RemoteSessionIssuersQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    RemoteSessionIssuersInfiniteQueryData,
    RemoteSessionIssuersPageParams
  >,
  RemoteSessionIssuersQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    RemoteSessionIssuersInfiniteQueryData,
    RemoteSessionIssuersQueryError,
    InfiniteData<
      RemoteSessionIssuersInfiniteQueryData,
      RemoteSessionIssuersPageParams
    >,
    QueryKey,
    RemoteSessionIssuersPageParams
  >({
    ...buildRemoteSessionIssuersInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setRemoteSessionIssuersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      search?: string | undefined;
      upstreamHost?: string | undefined;
      tier?: ListRemoteSessionIssuersQueryParamTier | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RemoteSessionIssuersQueryData,
): RemoteSessionIssuersQueryData | undefined {
  const key = queryKeyRemoteSessionIssuers(...queryKeyBase);

  return client.setQueryData<RemoteSessionIssuersQueryData>(key, data);
}

export function invalidateRemoteSessionIssuers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      search?: string | undefined;
      upstreamHost?: string | undefined;
      tier?: ListRemoteSessionIssuersQueryParamTier | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessionIssuers", "list", ...queryKeyBase],
  });
}

export function invalidateAllRemoteSessionIssuers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "remoteSessionIssuers", "list"],
  });
}
